import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ShieldAlert, Lock, Eye, AlertTriangle } from 'lucide-react';
import { siteConfig } from '../../data';
import ContactSection from '../../components/ContactSection';

export default function HjaelpModSvindel() {
  return (
    <div className="bg-white">
      <Helmet>
        <title>Hjælp mod svindel | Beskyt dig mod online bedrageri | Online Tryghed</title>
        <meta name="description" content="Er du blevet svindlet, eller er du bange for det? Vi hjælper dig med at sikre dine konti og lære at spotte svindlere." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-[#103B66] mb-6 leading-tight">
              Hjælp mod svindel: Stop bedragerne, før de stopper dig
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Svindlere bliver stadig mere sofistikerede. Uanset om det er via telefon, SMS, mail eller Facebook, står vi klar til at hjælpe dig med at gennemskue deres metoder.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`} className="px-8 py-4 bg-[#103B66] text-white font-bold rounded-sm hover:bg-slate-800 transition-all">
                Få hjælp nu
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="pt-10 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <div className="bg-white p-8 rounded-none border border-slate-100 shadow-sm">
                <h3 className="text-2xl font-bold text-[#103B66] mb-6">Vi hjælper med:</h3>
                <ul className="space-y-4">
                  {[
                    "Identificering af falske mails (Phishing)",
                    "Sikring af kompromitterede Facebook-profiler",
                    "Håndtering af mistænkelige bankopkald",
                    "Gennemgang af kontoudtog ved mistanke om misbrug",
                    "Kontakt til bank og myndigheder, hvis skaden er sket"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <AlertTriangle className="w-6 h-6 text-orange-500 mr-3 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl font-bold text-[#103B66] mb-6">Er skaden sket?</h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>
                  Hvis du er kommet til at give dine oplysninger væk eller har overført penge til en svindler, er det vigtigt at handle hurtigt. Vi hjælper dig med at bevare overblikket og tage de nødvendige skridt.
                </p>
                <p>
                  Vi guider dig gennem spærring af kort, ændring af adgangskoder og anmeldelse til politiet. Du står ikke alene med det.
                </p>
                <p className="font-semibold text-[#103B66]">
                  Husk: Banker og myndigheder vil ALDRIG bede om dine koder over telefonen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prevention */}
      <section className="py-20 bg-slate-50 text-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4 text-[#103B66]">Forebyggelse er den bedste beskyttelse</h2>
            <p className="text-slate-600">Lær at spotte de røde flag, før det er for sent.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 bg-white border border-slate-200 rounded-none shadow-sm">
              <Eye className="w-16 h-16 text-black mb-6" />
              <h3 className="text-xl font-bold mb-4 text-[#103B66]">Spot svindlen</h3>
              <p className="text-slate-600">Vi lærer dig de typiske tegn på svindel, så du kan føle dig tryg, når du modtager beskeder.</p>
            </div>
            <div className="p-8 bg-white border border-slate-200 rounded-none shadow-sm">
              <Lock className="w-16 h-16 text-black mb-6" />
              <h3 className="text-xl font-bold mb-4 text-[#103B66]">Sikre konti</h3>
              <p className="text-slate-600">Vi hjælper med at opsætte to-faktor-godkendelse, så svindlere ikke kan logge ind, selvom de har din kode.</p>
            </div>
            <div className="p-8 bg-white border border-slate-200 rounded-none shadow-sm">
              <ShieldAlert className="w-16 h-16 text-black mb-6" />
              <h3 className="text-xl font-bold mb-4 text-[#103B66]">Hurtig respons</h3>
              <p className="text-slate-600">Hav os på hurtigkald. Hvis du modtager noget mistænkeligt, kan du sende det til os for en vurdering.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Summary Section */}
      <section className="py-16 bg-white border-t border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#103B66] mb-4">Gennemskuelige priser på hjælp mod svindel</h2>
          <p className="text-slate-600 mb-10">
            Vi fakturerer pr. påbegyndt halve time, så du kun betaler for den tid, vi rent faktisk bruger.
          </p>
          <div className="grid sm:grid-cols-2 gap-6 mb-8 text-left">
            <div className="bg-slate-50 p-6 border border-slate-100">
              <h3 className="font-bold text-[#103B66] text-lg mb-2">Telefon & Fjernsupport</h3>
              <p className="text-2xl font-extrabold text-slate-900 mb-2">249,- <span className="text-sm font-normal text-slate-500">/ pr. påbegyndt 1/2 time</span></p>
              <p className="text-sm text-slate-500">Hurtig hjælp over telefonen eller via sikker fjernstyring.</p>
            </div>
            <div className="bg-slate-50 p-6 border border-slate-100">
              <h3 className="font-bold text-[#103B66] text-lg mb-2">Personligt fremmøde</h3>
              <p className="text-2xl font-extrabold text-slate-900 mb-2">349,- <span className="text-sm font-normal text-slate-500">/ pr. påbegyndt 1/2 time</span></p>
              <p className="text-sm text-slate-500">Vi kommer hjem til dig. Kørsel: 10,- pr. km (kun udrejsen fra Slagelse).</p>
            </div>
          </div>
          <p className="text-xs text-slate-400">Alle priser er inkl. moms. Vi fakturerer pr. påbegyndt halve time.</p>
        </div>
      </section>

      <ContactSection />
    </div>
  );
}
